import { Link, Mail, Phone, Store } from 'lucide-react';
import { useState } from 'react';
import useEntity from '../hooks/useEntity';
import { entityService } from '../services/entityService';
import useToastStore from '../store/useToastStore';

interface ConfirmationModalProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: () => void;
  title: string;
  message: string;
  confirmText: string;
  cancelText: string;
}

const ConfirmationModal = ({ isOpen, onClose, onConfirm, title, message, confirmText, cancelText }: ConfirmationModalProps) => {
  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-gray-600 bg-opacity-50 flex justify-center items-center p-4 z-50">
      <div className="bg-white rounded-lg shadow-lg p-6 w-full max-w-md">
        <h2 className="text-lg font-semibold mb-4">{title}</h2>
        <p className="mb-6 text-gray-600">{message}</p>
        <div className="flex flex-col-reverse sm:flex-row justify-end gap-3">
          <button
            onClick={onClose}
            className="px-4 py-2 bg-gray-200 text-gray-800 rounded-lg hover:bg-gray-300 w-full sm:w-auto"
          >
            {cancelText}
          </button>
          <button
            onClick={onConfirm}
            className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 w-full sm:w-auto"
          >
            {confirmText}
          </button>
        </div>
      </div>
    </div>
  );
};

const Settings = () => {
  const { data: entity, refetch, isLoading: userLoading } = useEntity();
  const [isUpdating, setIsUpdating] = useState(false);
  const [newStoreUrl, setNewStoreUrl] = useState('');
  const [showStatusModal, setShowStatusModal] = useState(false);
  const [showUrlModal, setShowUrlModal] = useState(false);
  const [isEditingUrl, setIsEditingUrl] = useState(false);
  const [urlError, setUrlError] = useState('');
  const [isUrlValid, setIsUrlValid] = useState(false);
  const toast = useToastStore();

  // Validation function for subdomain
  const validateSubdomain = (url: string) => {
    if (!url.trim()) {
      setUrlError('');
      setIsUrlValid(false);
      return;
    }

    // Subdomain validation rules
    const subdomainRegex = /^[a-z0-9]([a-z0-9-]*[a-z0-9])?$/;

    if (url.length < 3) {
      setUrlError('Subdomain must be at least 3 characters long');
      setIsUrlValid(false);
      return;
    }

    if (url.length > 63) {
      setUrlError('Subdomain must be less than 63 characters');
      setIsUrlValid(false);
      return;
    }

    if (!subdomainRegex.test(url)) {
      setUrlError('Subdomain can only contain lowercase letters, numbers, and hyphens. Cannot start or end with a hyphen.');
      setIsUrlValid(false);
      return;
    }

    // Check for consecutive hyphens
    if (url.includes('--')) {
      setUrlError('Subdomain cannot contain consecutive hyphens');
      setIsUrlValid(false);
      return;
    }

    // Reserved subdomain check (you can add more as needed)
    const reservedSubdomains = ['www', 'api', 'admin', 'mail', 'ftp', 'blog', 'shop', 'store', 'app'];
    if (reservedSubdomains.includes(url.toLowerCase())) {
      setUrlError('This subdomain is reserved and cannot be used');
      setIsUrlValid(false);
      return;
    }

    setUrlError('');
    setIsUrlValid(true);
  };

  // Handle URL input change with validation
  const handleUrlChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, '');
    setNewStoreUrl(value);
    validateSubdomain(value);
  };

  const handleToggleStoreStatus = async () => {
    try {
      setIsUpdating(true);
      await entityService.updateEntityStoreStatus(!entity!.isActive);
      await refetch();
      setShowStatusModal(false);
      toast.showToast(
        `Store successfully ${entity?.isActive ? 'deactivated' : 'activated'}.`,
        'success'
      );
    } catch (error) {
      setShowStatusModal(false);
      console.error('Error toggling store status:', error);
      toast.showToast(
        error instanceof Error ? error.message : 'An unknown error occurred',
        'error'
      );
    } finally {
      setIsUpdating(false);
    }
  };

  const handleUpdateStoreUrl = async () => {
    if (!newStoreUrl) return;

    if (!isUrlValid) {
      toast.showToast('Please enter a valid subdomain', 'error');
      return;
    }

    try {
      setIsUpdating(true);
      await entityService.updateEntityStoreUrl({ storeUrl: newStoreUrl });
      await refetch();
      setNewStoreUrl('');
      setShowUrlModal(false);
      setUrlError('');
      setIsUrlValid(false);
      setIsEditingUrl(false); // Exit edit mode on success
      toast.showToast('Store URL updated successfully.', 'success');
    } catch (error: any) {
      setShowUrlModal(false);
      console.error('Error updating store URL:', error);
      let errorMessage = 'Failed to update store URL. Please try again later.';

      // Handle specific error cases
      if (error?.message) {
        if (error.message.includes('already')) {
          errorMessage = 'This store URL is already taken. Please choose a different one.';
        } else if (error.message.includes('invalid')) {
          errorMessage = 'Invalid store URL. Please use only letters, numbers, and hyphens.';
        }
      }

      toast.showToast(errorMessage, 'error');
    } finally {
      setIsUpdating(false);
    }
  };

  const handleCancelEdit = () => {
    setIsEditingUrl(false);
    setNewStoreUrl('');
    setUrlError('');
    setIsUrlValid(false);
  };

  if (userLoading) {
    return (
      <div className="flex justify-center items-center h-screen">
        Loading...
      </div>
    );
  }

  return (
    <div className="bg-gray-50 min-h-screen">
      <div className="max-w-4xl mx-auto p-4 sm:p-6 lg:p-8 space-y-6">
        {/* Store Status */}
        <div className="bg-white rounded-xl overflow-hidden shadow-sm border border-gray-100">
          <div className="p-4 sm:p-6">
            <div className="flex items-center justify-between mb-2">
              <div className="flex items-center gap-3">
                <div className="h-10 w-10 rounded-full bg-green-100 flex-shrink-0 flex items-center justify-center">
                  <Store className="h-5 w-5 text-green-600" />
                </div>
                <h3 className="text-lg font-medium text-gray-900">Store Status</h3>
              </div>
              <button
                onClick={() => setShowStatusModal(true)}
                disabled={isUpdating}
                className={`relative w-14 h-7 rounded-full transition duration-200 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 ${entity?.isActive ? "bg-blue-600" : "bg-gray-200"
                  }`}
              >
                <div
                  className={`absolute w-5 h-5 bg-white rounded-full shadow-md top-1 transition-all duration-200 ${entity?.isActive ? "right-1" : "left-1"
                    }`}
                ></div>
              </button>
            </div>

            <p className="text-sm text-gray-600 mb-4">
              {entity?.isActive
                ? "Your store is currently active and visible to customers."
                : "Your store is currently inactive and hidden from customers."}
            </p>
          </div>
        </div>

        {/* Store URL */}
        <div className="bg-white rounded-xl overflow-hidden shadow-sm border border-gray-100">
          <div className="p-4 sm:p-6">
            <div className="flex items-center justify-between mb-2">
              <div className="flex items-center gap-3">
                <div className="h-10 w-10 rounded-full bg-blue-100 flex-shrink-0 flex items-center justify-center">
                  <Link className="h-5 w-5 text-blue-600" />
                </div>
                <h3 className="text-lg font-medium text-gray-900">Store URL</h3>
              </div>
              {!isEditingUrl && (
                <button
                  onClick={() => {
                    setIsEditingUrl(true);
                    setNewStoreUrl(entity?.storeUrl || '');
                  }}
                  className="px-3 py-1.5 bg-blue-50 text-blue-600 rounded-lg hover:bg-blue-100 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition-colors duration-200 text-sm font-medium"
                >
                  Edit
                </button>
              )}
            </div>

            <p className="text-sm text-gray-600 mb-4">
              Your custom web address for your store
            </p>

            {!isEditingUrl ? (
              <div className="flex items-center gap-1 text-sm">
                <span className="text-gray-500">https://</span>
                <span className="font-medium text-gray-900">{entity?.storeUrl}</span>
                <span className="text-gray-500">.mart-24.com</span>
              </div>
            ) : (
              <div className="mt-4 space-y-3">
                <div className="flex flex-col sm:flex-row gap-2">
                  <input
                    type="text"
                    value={newStoreUrl}
                    onChange={handleUrlChange}
                    placeholder="Enter new store URL"
                    className={`flex-1 px-3 py-2 border rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-offset-2 ${urlError
                      ? 'border-red-300 focus:border-red-500 focus:ring-red-100'
                      : isUrlValid
                        ? 'border-green-300 focus:border-green-500 focus:ring-green-100'
                        : 'border-gray-300 focus:border-blue-500 focus:ring-blue-100'
                      }`}
                  />
                  <div className="flex gap-2">
                    <button
                      onClick={() => setShowUrlModal(true)}
                      disabled={isUpdating || !newStoreUrl || !isUrlValid}
                      className="px-4 py-2 bg-blue-600 text-white text-sm font-medium rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      Save
                    </button>
                    <button
                      onClick={handleCancelEdit}
                      className="px-4 py-2 text-gray-700 text-sm font-medium rounded-md hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-gray-500 focus:ring-offset-2"
                    >
                      Cancel
                    </button>
                  </div>
                </div>
                {urlError && (
                  <p className="text-sm text-red-600 flex items-center gap-1">
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                    {urlError}
                  </p>
                )}
                {isUrlValid && newStoreUrl && (
                  <p className="text-sm text-green-600 flex items-center gap-1">
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                    Valid subdomain
                  </p>
                )}
              </div>
            )}
          </div>
        </div>

        {/* Mobile Number */}
        <div className="bg-white rounded-xl overflow-hidden shadow-sm border border-gray-100">
          <div className="p-4 sm:p-6">
            <div className="flex items-center justify-between mb-2">
              <div className="flex items-center gap-3">
                <div className="h-10 w-10 rounded-full bg-orange-100 flex-shrink-0 flex items-center justify-center">
                  <Phone className="h-5 w-5 text-orange-600" />
                </div>
                <h3 className="text-lg font-medium text-gray-900">Mobile Number</h3>
              </div>
            </div>

            <p className="text-sm text-gray-600 mb-4">
              Your contact number for customers
            </p>

            <div className="flex items-center gap-1 text-sm">
              <span className="font-medium text-gray-900">{entity?.mobile || 'Not set'}</span>
            </div>
          </div>
        </div>

        {/* Account Information */}
        <div className="bg-white rounded-xl overflow-hidden shadow-sm border border-gray-100">
          <div className="p-4 sm:p-6">
            <div className="flex items-start gap-3 mb-4">
              <Mail className="w-5 h-5 text-gray-500 mt-0.5" />
              <div>
                <h3 className="text-base sm:text-lg font-medium text-gray-900">Account Information</h3>
              </div>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="p-3 bg-gray-50 rounded-md">
                <p className="text-xs font-medium text-gray-500 uppercase tracking-wider">Email</p>
                <p className="mt-1 text-sm text-gray-900 break-all">{entity?.email}</p>
              </div>
              <div className="p-3 bg-gray-50 rounded-md">
                <p className="text-xs font-medium text-gray-500 uppercase tracking-wider">Name</p>
                <p className="mt-1 text-sm text-gray-900">{entity?.name}</p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Status Toggle Confirmation Modal */}
      <ConfirmationModal
        isOpen={showStatusModal}
        onClose={() => setShowStatusModal(false)}
        onConfirm={handleToggleStoreStatus}
        title={entity?.isActive ? "Deactivate Store?" : "Activate Store?"}
        message={entity?.isActive
          ? "Are you sure you want to deactivate your store? This will make it invisible to customers."
          : "Are you sure you want to activate your store? This will make it visible to customers."}
        confirmText={entity?.isActive ? "Deactivate" : "Activate"}
        cancelText="Cancel"
      />

      {/* URL Update Confirmation Modal */}
      <ConfirmationModal
        isOpen={showUrlModal}
        onClose={() => setShowUrlModal(false)}
        onConfirm={handleUpdateStoreUrl}
        title="Update Store URL?"
        message={`Are you sure you want to change your store URL to "${newStoreUrl}"? This will affect your store's web address.`}
        confirmText="Update URL"
        cancelText="Cancel"
      />

    </div>
  );
};

export default Settings;
